import { HttpClient } from '@angular/common/http';
import { SwPush } from '@angular/service-worker';
import { Observable } from 'rxjs';
import { AppMonitoring } from '../../app-services/monitoring/monitor.app';
import { AbstractEndpoints } from '../endpoints/endpoints.service.abstract';
import { AbstractServiceNotification } from './notification.service.abstract';
import * as Models from '../../../app/models/models';
import { AppConfig } from '../../app-services/config/config.app';
import { AppSnackBar } from '../../app-services/snackbar/snackbar.app';
import { SnackBarNotificationRequestComponent } from '../../snack-bar-components/snack-bar-notification-request/snack-bar-notification-request.component';
import { Enums } from '../../enums/enums';
import { AbstractServiceAzureStorage } from '../azure-storage.service.ts/azure-storage.service.abstract';
import { ModelInitializer } from '../../models/model-initializer.helper';

export class ServiceNotification implements AbstractServiceNotification {

    constructor(
        private readonly http: HttpClient,
        private readonly endpoints: AbstractEndpoints,
        private readonly appConfig: AppConfig,
        private readonly appMonitoring: AppMonitoring,
        private readonly swPush: SwPush,
        private readonly appSnackbar: AppSnackBar,
        private readonly serviceAzureStorage: AbstractServiceAzureStorage
    ) {
        this.subscribeToNotifications();
    }

    subscribeToNotifications(): void {
        this.appMonitoring.logEvent(this, "Subscribing to notifications");
        this.swPush.messages.subscribe((message) => {
            this.appMonitoring.logEvent(this, "Notification received: " + JSON.stringify(message));
        });
    }


    /**
     * Asks the user if they want to receive notifications for a campaign using SwPush.
     * Stores a timestamped GUID in localStorage if the user accepts.
     * Ensures the user is not asked again if already subscribed.
     * @param campaignId GUID associated with the campaign.
     */
    askForNotificationSubscription(organizationId: string, campaignId: string, notificationMessage: string): void {
        const organizationKey = `${Enums.LocalStorageData.notificationSubscriptionOrganizationPrefix}${organizationId}`;
        const campaignKey = `${Enums.LocalStorageData.notificationSubscriptionCampaignPrefix}${campaignId}`;

        if (localStorage.getItem(campaignKey)) {
            this.appMonitoring.logEvent(this, 'User is already subscribed to notifications for this campaign.');
            return;
        }

        try {
            setTimeout(() => {
                this.appMonitoring.logEvent(this, "Timeout started, showing snackbar");
                this.appSnackbar.showSnackbar(Enums.SnackbarType.NotificationRequest,
                    SnackBarNotificationRequestComponent,
                    notificationMessage,
                    async (consent: boolean) => {
                        if (consent) {
                            const subscription = await this.swPush.requestSubscription({
                                serverPublicKey: this.appConfig.vapidKey,
                            });
                            if (!localStorage.getItem(organizationKey)) {
                                localStorage.setItem(organizationKey, `${organizationId}`);
                                this.serviceAzureStorage.appendSubscription(
                                    organizationId,
                                    ModelInitializer.SubscriptionRecord({ organizationId, pushSubscription: subscription })
                                );
                            }
                            localStorage.setItem(campaignKey, `${organizationId}-${campaignId}`);
                            this.serviceAzureStorage.appendSubscription(
                                `${organizationId}/${campaignId}`,
                                ModelInitializer.SubscriptionRecord({ organizationId, campaignId, pushSubscription: subscription })
                            );
                        }
                    })
            }, 2000);
        } catch (error) {
            this.appMonitoring.logException(this, 'Failed to subscribe to notifications:', { error });
        }
    }

    sendNotifications(sendNotificationParameter: Models.NotificationConfiguration): Observable<Models.ResponseSendNotification> {
        return this.http.post<Models.ResponseSendNotification>(this.endpoints.notifications.sendNotification, sendNotificationParameter);
    }
}